import React, { useState } from 'react'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { PencilLine, Percent } from 'lucide-react'
import { OffsetInput } from '@/components/account/groups/RoomMappingRoomDialog'
import { offsetLabel, offsetProblem, type OffsetDraft } from '@/lib/room-mapping'

interface EveryRoomOffsetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  listingName: string
  leadName: string
  /** How many rooms are staged as Same rooms; each gets the offset. */
  sameRoomCount: number
  currencySymbol: string
  onApply: (offset: OffsetDraft) => void
}

// "SAME OFFSET FOR EVERY SAME ROOM" (combined inventory §3.20): one offset staged on every Same room of the
// listing at once, the way a listing is usually priced against the lead. Each room is still its own row,
// so one can differ afterwards, and nothing saves until Review & Save.
export default function EveryRoomOffsetDialog({
  open,
  onOpenChange,
  listingName,
  leadName,
  sameRoomCount,
  currencySymbol: sym,
  onApply,
}: EveryRoomOffsetDialogProps) {
  const [draft, setDraft] = useState<OffsetDraft>({ offsetType: 'percentage', offsetValue: '' })
  const [error, setError] = useState<string | null>(null)
  const label = offsetLabel(draft, sym)

  function handleApply() {
    const problem = offsetProblem(draft)
    if (problem) {
      setError(problem)
      return
    }
    onApply({ ...draft, offsetValue: draft.offsetValue.trim() })
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Percent className="h-4 w-4 text-primary" aria-hidden="true" />
            Same offset for every Same room
          </DialogTitle>
          <DialogDescription className="text-xs">
            {sameRoomCount === 1 ? 'The Same room' : `All ${sameRoomCount} Same rooms`} at {listingName} sell at the price of the
            same room at {leadName}, plus this offset. You can still change any one of them afterwards.
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-2">
          <Label htmlFor="every-room-offset" className="text-sm font-semibold text-foreground">
            Offset
          </Label>
          <OffsetInput
            id="every-room-offset"
            draft={draft}
            sym={sym}
            invalid={!!error}
            onChange={(next) => {
              setDraft((prev) => ({ ...prev, ...next }))
              setError(null)
            }}
          />
          {error ? (
            <p className="text-[11px] font-medium text-rose-600">{error}</p>
          ) : (
            <p className="text-xs text-muted-foreground">
              {label ? `Every Same room sells at its lead room's price ${label}.` : 'Every Same room sells at its lead room’s price.'}{' '}
              Use a minus for a lower price; leave it empty for none.
            </p>
          )}
        </DialogBody>

        <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <PencilLine className="h-3.5 w-3.5 shrink-0 text-amber-500" aria-hidden="true" />
            Staged only, not saved yet.
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" className="rounded-lg" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" className="rounded-lg" onClick={handleApply}>
              Apply to every Same room
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
